import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';
import { GuestConvertAccountModal } from './GuestConvertAccountModal';

interface GuestArchiveItem {
  puzzleNumber: number;
  publishDate: string;
  played: boolean;
  runId: string | null;
}

interface GuestStatus {
  isGuest: boolean;
  dailyCompletedToday: boolean;
  guestArchives: GuestArchiveItem[];
  archivesPlayedCount: number;
  archivesTotal: number;
  nextArchivePuzzleNumber: number | null;
  shouldPromptConvert: boolean;
}

interface GuestOnboardingBlockProps {
  isArchive?: boolean;
}

export function GuestOnboardingBlock({ isArchive = false }: GuestOnboardingBlockProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<GuestStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConvert, setShowConvert] = useState(false);

  useEffect(() => {
    if (!user?.isGuest) {
      setLoading(false);
      return;
    }
    api<GuestStatus>('/onboarding/guest-status')
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [user?.isGuest]);

  if (!user?.isGuest || loading || !status?.isGuest) return null;

  const { archivesPlayedCount, archivesTotal, nextArchivePuzzleNumber, shouldPromptConvert } = status;
  const progressPct = archivesTotal > 0
    ? Math.round((archivesPlayedCount / archivesTotal) * 100)
    : 0;

  const goNextArchive = () => {
    if (nextArchivePuzzleNumber) {
      navigate(`/archives/${nextArchivePuzzleNumber}/play`);
    }
  };

  const convertModal = showConvert ? (
    <GuestConvertAccountModal onClose={() => setShowConvert(false)} />
  ) : null;

  const convertTeaser = !shouldPromptConvert ? (
    <div className="guest-onboarding-convert-inline">
      <p className="guest-onboarding-convert-hint">{t('onboarding.convertTeaser')}</p>
      <button type="button" className="btn secondary block" onClick={() => setShowConvert(true)}>
        {t('onboarding.convertCta')}
      </button>
    </div>
  ) : null;

  if (shouldPromptConvert) {
    return (
      <div className="guest-onboarding guest-onboarding--convert">
        <h2 className="guest-onboarding-title">{t('onboarding.convertTitle')}</h2>
        <ul className="guest-onboarding-benefits">
          <li>{t('onboarding.convertBenefit1')}</li>
          <li>{t('onboarding.convertBenefit2')}</li>
        </ul>
        <button type="button" className="btn primary block" onClick={() => setShowConvert(true)}>
          {t('onboarding.convertCta')}
        </button>
        {convertModal}
      </div>
    );
  }

  if (!isArchive && status.dailyCompletedToday && nextArchivePuzzleNumber) {
    return (
      <div className="guest-onboarding">
        <h2 className="guest-onboarding-title">{t('onboarding.guestArchivesTitle')}</h2>
        <p className="muted">{t('onboarding.guestArchivesHint')}</p>
        <button type="button" className="btn primary block" onClick={goNextArchive}>
          {t('onboarding.guestArchivesCta')}
        </button>
        {convertTeaser}
        {convertModal}
      </div>
    );
  }

  if (isArchive && archivesPlayedCount > 0 && archivesPlayedCount < archivesTotal && nextArchivePuzzleNumber) {
    return (
      <div className="guest-onboarding">
        <p className="guest-onboarding-progress-label">
          {t('onboarding.progress')
            .replace('{done}', String(archivesPlayedCount))
            .replace('{total}', String(archivesTotal))}
        </p>
        <div className="guest-onboarding-progress" aria-hidden="true">
          <div className="guest-onboarding-progress-bar" style={{ width: `${progressPct}%` }} />
        </div>
        <button type="button" className="btn primary block" onClick={goNextArchive}>
          {t('onboarding.nextArchive')} →
        </button>
        {convertTeaser}
        {convertModal}
      </div>
    );
  }

  if (isArchive && archivesPlayedCount === 0 && nextArchivePuzzleNumber) {
    return (
      <div className="guest-onboarding">
        <h2 className="guest-onboarding-title">{t('onboarding.guestArchivesTitle')}</h2>
        <Link to={`/archives/${nextArchivePuzzleNumber}/play`} className="btn primary block">
          {t('onboarding.guestArchivesCta')}
        </Link>
        {convertTeaser}
        {convertModal}
      </div>
    );
  }

  return null;
}

export function GuestOnboardingBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState<GuestStatus | null>(null);

  useEffect(() => {
    if (!user?.isGuest) return;
    api<GuestStatus>('/onboarding/guest-status')
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [user?.isGuest]);

  if (!user?.isGuest || !status?.isGuest) return null;
  if (status.shouldPromptConvert) return null;
  if (!status.nextArchivePuzzleNumber) return null;

  return (
    <div className="archives-guest-onboarding">
      <p>{t('onboarding.guestArchivesBanner')}</p>
      <Link to={`/archives/${status.nextArchivePuzzleNumber}/play`} className="btn secondary admin-btn-sm">
        {t('onboarding.guestArchivesCta')}
      </Link>
    </div>
  );
}

export function GuestAuthRequiredModal({ onClose }: { onClose: () => void }) {
  return (
    <GuestConvertAccountModal onClose={onClose} />
  );
}
